// Survey completion time estimation utility
// Includes both reading time and estimated time to fill each field type

/**
 * Calculates the estimated time to complete a survey based on field types
 * @param customFields - Array of survey custom fields
 * @returns Formatted time string (e.g., "Approx 5 mins") or empty string
 */
export const calculateSurveyTime = (customFields: any[]): string => {
  if (!customFields || customFields.length === 0) return '';

  let totalSeconds = 0;

  // Estimated time for each field type (in seconds)
  const fieldTimeEstimates: Record<string, number> = {
    string: 15, // Short text input - 15 seconds
    text: 120, // Long text input - 2 minutes (users typically write 100-200 words)
    email: 10, // Email input - 10 seconds
    url: 12, // URL input - 12 seconds
    phone: 10, // Phone input - 10 seconds
    number: 8, // Number input - 8 seconds
    date: 8, // Date picker - 8 seconds
    enum_boolean: 5, // Yes/No - 5 seconds
    rel_tag: 10, // Select/Radio - 10 seconds (reading + selecting)
    star_rating: 8, // Star rating - 8 seconds
    scale_rating: 8, // Scale rating - 8 seconds
    nps_rating: 8, // NPS rating - 8 seconds
    rel_file: 45, // File upload - 45 seconds (finding and uploading file)
    default: 12, // Default for unknown types
  };

  customFields.forEach((field: any) => {
    // Add time to read the question
    const questionText = [field.label || '', field.description || ''].join(' ');

    const words = questionText
      .trim()
      .split(/\s+/)
      .filter((w) => w.length > 0);
    const readingTimeSeconds = Math.ceil((words.length / 150) * 60); // 150 words per minute (survey comprehension speed)
    totalSeconds += readingTimeSeconds;

    // Add time to read options for select/radio2 fields
    if (
      field.kind === 'rel_tag' &&
      field.values &&
      Array.isArray(field.values)
    ) {
      const optionsText = field.values.map((v: any) => v.value || '').join(' ');
      const optionWords = optionsText
        .trim()
        .split(/\s+/)
        .filter((w) => w.length > 0);
      const optionsReadingTime = Math.ceil((optionWords.length / 150) * 60);
      totalSeconds += optionsReadingTime;
    }

    // Add estimated time to complete this field
    const fieldTime =
      fieldTimeEstimates[field.kind] || fieldTimeEstimates.default;
    totalSeconds += fieldTime;
  });

  // Convert to minutes
  const minutes = Math.ceil(totalSeconds / 60);

  // Don't show time for very short surveys (less than 1 minute)
  if (minutes < 1) return '';

  // Format output
  if (minutes === 1) {
    return 'Approx 1 min';
  }
  return `Approx ${minutes} mins`;
};
